/* eslint-disable */
import { useRef, useState, useCallback, useEffect } from 'react';
import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSession,
  AudioVideoFacade,
  VideoTileState,
} from 'amazon-chime-sdk-js';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export interface UseChimeSessionOptions {
  sessionId?: string;
  existingMeetingId?: string;
  autoConnect?: boolean;
  publishAudio?: boolean;
  publishVideo?: boolean;
}

export interface UseChimeSessionReturn {
  localVideoRef: React.RefObject<HTMLVideoElement | null>;
  remoteVideoRef: React.RefObject<HTMLVideoElement | null>;
  isConnected: boolean;
  isPublishing: boolean;
  connectionQuality: 'excellent' | 'poor' | 'reconnecting';
  error: string | null;
  activeMeetingId: string | null;
  connect: (opts?: { sessionId?: string; role?: 'coach' | 'client' }) => Promise<void>;
  disconnect: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  rebindVideo: () => void;
  audioEnabled: boolean;
  videoEnabled: boolean;
  isRemoteVideoActive: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
  sendChatMessage: (text: string) => void;
  chatMessages: { text: string; sender: string; timestamp: number }[];
  isRecording: boolean;
  sendCaption: (text: string) => void;
  remoteCaption: string;
}

export function useChimeSession(options: UseChimeSessionOptions = {}): UseChimeSessionReturn {
  const {
    sessionId,
    existingMeetingId,
    autoConnect = false,
    publishAudio = true,
    publishVideo = true,
  } = options;

  const { user } = useAuth();
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<'excellent' | 'poor' | 'reconnecting'>('excellent');
  const [error, setError] = useState<string | null>(null);
  const [activeMeetingId, setActiveMeetingId] = useState<string | null>(existingMeetingId || null);
  const [audioEnabled, setAudioEnabled] = useState(publishAudio);
  const [videoEnabled, setVideoEnabled] = useState(publishVideo);
  const [isRemoteVideoActive, setIsRemoteVideoActive] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ text: string; sender: string; timestamp: number }[]>([]);
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const [activeRecordingDbId, setActiveRecordingDbId] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [remoteCaption, setRemoteCaption] = useState('');

  const meetingSessionRef = useRef<MeetingSession | null>(null);
  const audioVideoRef = useRef<AudioVideoFacade | null>(null);
  const isConnectingRef = useRef<boolean>(false);

  /** Get the Supabase access token for authenticated API calls */
  const getAccessToken = async (): Promise<string | null> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) return session.access_token;
    } catch {}
    // Fallback to localStorage token
    const token = localStorage.getItem('whipt_session_token');
    return token;
  };

  const connect = useCallback(async (opts?: { sessionId?: string; role?: 'coach' | 'client' }) => {
    if (isConnectingRef.current || meetingSessionRef.current) return;
    isConnectingRef.current = true;

    try {
      setError(null);
      console.log('🎥 Initializing Chime SDK session...');

      const token = await getAccessToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const joinSessionId = opts?.sessionId || sessionId;
      const role = opts?.role || 'coach';
      let response;

      if (joinSessionId) {
        response = await fetch(`/api/video/join-session`, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ sessionId: joinSessionId, role })
        });
      } else if (existingMeetingId) {
        response = await fetch(`/api/video/join-meeting`, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({ meetingId: existingMeetingId })
        });
      } else {
        response = await fetch(`/api/video/create-meeting`, {
          method: "POST",
          headers,
          credentials: "include",
        });
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join/create meeting');
      }

      console.log("🚀 Live Amazon Chime Meeting Session ID:", data?.Meeting?.MeetingId || 'mock-meeting');
      setActiveMeetingId(data?.Meeting?.MeetingId || 'mock-meeting-123');
      setIsConnected(true);

      if (data?.Meeting) {
        const logger = new ConsoleLogger('ChimeLogger', LogLevel.INFO);
        const deviceController = new DefaultDeviceController(logger);

        const configuration = new MeetingSessionConfiguration(
          data.Meeting,
          data.Attendee
        );

        const meetingSession = new DefaultMeetingSession(
          configuration,
          logger,
          deviceController
        );
        meetingSessionRef.current = meetingSession;
        audioVideoRef.current = meetingSession.audioVideo;

        const audioVideo = meetingSession.audioVideo;

        audioVideo.addObserver({
          audioVideoDidStart: () => {
            console.log('✅ Chime session connected');
            setIsConnected(true);
            setConnectionQuality('excellent');
          },
          audioVideoDidStop: (sessionStatus: any) => {
            console.log('❌ Chime session stopped. Status code:', sessionStatus?.statusCode());
            const code = sessionStatus?.statusCode();
            if (code !== 0 && code !== 1) {
              setConnectionQuality('reconnecting');
            } else {
              setIsConnected(false);
              setIsPublishing(false);
            }
          },
          connectionDidBecomePoor: () => {
            setConnectionQuality('poor');
          },
          connectionDidBecomeGood: () => {
            setConnectionQuality('excellent');
          },
          videoTileDidUpdate: (tileState: VideoTileState) => {
            if (!tileState.boundAttendeeId || tileState.tileId === null) return;

            if (tileState.localTile && localVideoRef.current) {
              audioVideo.bindVideoElement(tileState.tileId, localVideoRef.current);
            } else if (!tileState.localTile && remoteVideoRef.current) {
              audioVideo.bindVideoElement(tileState.tileId, remoteVideoRef.current);
              setIsRemoteVideoActive(true);
            }
          },
          videoTileWasRemoved: (tileId: number) => {
            const tiles = audioVideo.getAllVideoTiles();
            const hasRemote = tiles.some(t => !t.state().localTile);
            setIsRemoteVideoActive(hasRemote);
          }
        });

        try {
          const audioInputDevices = await audioVideo.listAudioInputDevices();
          if (audioInputDevices.length > 0) {
            await audioVideo.startAudioInput(audioInputDevices[0].deviceId);
          }

          const videoInputDevices = await audioVideo.listVideoInputDevices();
          if (videoInputDevices.length > 0) {
            await audioVideo.startVideoInput(videoInputDevices[0].deviceId);
          }

          audioVideo.start();
          setIsPublishing(true);
          setIsConnected(true);

          if (videoEnabled) {
            audioVideo.startLocalVideoTile();
          }
          if (!audioEnabled) {
            audioVideo.realtimeMuteLocalAudio();
          }

          // Subscribe to incoming data messages (chat, system events)
          audioVideo.realtimeSubscribeToReceiveDataMessage('chat', (dataMessage: any) => {
            try {
              const payload = JSON.parse(dataMessage.text);
              if (payload.text) {
                setChatMessages(prev => [...prev, {
                  text: payload.text,
                  sender: payload.sender || 'Other',
                  timestamp: payload.timestamp || Date.now()
                }]);
              }
            } catch (e) {
              console.warn('[chime] Failed to parse chat data message:', e);
            }
          });

          // Subscribe to incoming caption data messages (relayed from remote peer)
          audioVideo.realtimeSubscribeToReceiveDataMessage('caption', (dataMessage: any) => {
            try {
              const payload = JSON.parse(dataMessage.text);
              if (payload.text) {
                setRemoteCaption(payload.text);
              }
            } catch (e) {
              console.warn('[chime] Failed to parse caption data message:', e);
            }
          });
        } catch (err: any) {
          console.warn('⚠️ Chime audio/video start failed:', err);
          setError(err?.message || 'Could not start audio/video');
          isConnectingRef.current = false;
        }
        isConnectingRef.current = false;
      } else {
        setError('No meeting data returned from server');
        isConnectingRef.current = false;
      }
    } catch (err: any) {
      console.warn('⚠️ Failed to connect to session:', err);
      setError(err?.message || 'Could not connect to video session');
      isConnectingRef.current = false;
    }
  }, [audioEnabled, videoEnabled, existingMeetingId, sessionId]);

  const disconnect = useCallback(() => {
    console.log('🛑 Disconnecting Chime session and releasing hardware...');

    // Stop any camera/mic tracks on the video elements explicitly
    [localVideoRef, remoteVideoRef].forEach(ref => {
      if (ref.current) {
        const stream = (ref.current as HTMLVideoElement).srcObject as MediaStream | null;
        if (stream) {
          stream.getTracks().forEach(track => {
            track.stop();
          });
          (ref.current as HTMLVideoElement).srcObject = null;
        }
      }
    });

    if (audioVideoRef.current) {
      try {
        audioVideoRef.current.stopLocalVideoTile();
        audioVideoRef.current.stopVideoInput();
        audioVideoRef.current.stopAudioInput();
      } catch (e) {
        console.warn('Failed to cleanly release hardware inputs:', e);
      }
      audioVideoRef.current.stop();
      audioVideoRef.current = null;
    }
    meetingSessionRef.current = null;
    isConnectingRef.current = false;
    setIsConnected(false);
    setIsPublishing(false);
    setIsRemoteVideoActive(false);
  }, []);

  const toggleAudio = useCallback(() => {
    if (audioVideoRef.current) {
      const newState = !audioEnabled;
      if (newState) {
        audioVideoRef.current.realtimeUnmuteLocalAudio();
      } else {
        audioVideoRef.current.realtimeMuteLocalAudio();
      }
      setAudioEnabled(newState);
    }
  }, [audioEnabled]);

  const toggleVideo = useCallback(() => {
    if (audioVideoRef.current) {
      const newState = !videoEnabled;
      if (newState) {
        audioVideoRef.current.startLocalVideoTile();
      } else {
        audioVideoRef.current.stopLocalVideoTile();
      }
      setVideoEnabled(newState);
    }
  }, [videoEnabled]);

  const rebindVideo = useCallback(() => {
    console.log("🔄 Re-binding video tiles to DOM elements...");
    if (audioVideoRef.current) {
      const tiles = audioVideoRef.current.getAllVideoTiles();
      tiles.forEach(tile => {
        const state = tile.state();
        if (state.localTile && localVideoRef.current) {
          audioVideoRef.current?.bindVideoElement(state.tileId!, localVideoRef.current);
          localVideoRef.current.play().catch(() => {});
        } else if (!state.localTile && remoteVideoRef.current) {
          audioVideoRef.current?.bindVideoElement(state.tileId!, remoteVideoRef.current);
          remoteVideoRef.current.play().catch(() => {});
        }
      });
    }

    if (isConnected && audioVideoRef.current && localVideoRef.current && (!localVideoRef.current.srcObject || (localVideoRef.current.srcObject as MediaStream).active === false)) {
      console.warn('[rebindVideo] Local video element has no active stream — Chime tile may not have bound yet');
    }
  }, [isConnected]);

  useEffect(() => {
    if (autoConnect && !meetingSessionRef.current) {
      connect();
    }
    return () => {
      if (meetingSessionRef.current) {
        disconnect();
      }
    };
  }, [autoConnect, connect, disconnect]);

  const startRecording = useCallback(async () => {
    if (!activeMeetingId) return;
    try {
      const token = await getAccessToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      console.log('Initiating recording for meeting:', activeMeetingId);
      const response = await fetch(`/api/video/start-recording`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          meetingId: activeMeetingId,
          sessionTitle: 'Whipt Session',
          clientName: 'Participant',
        })
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to start recording');
      }
      console.log('Recording started, pipeline ID:', data.mediaPipelineId);
      setActivePipelineId(data.mediaPipelineId);
      setIsRecording(true);

      if (data.recordingId) {
        setActiveRecordingDbId(data.recordingId);
      }

      // Notify other clients via data message that recording has started
      if (audioVideoRef.current) {
        audioVideoRef.current.realtimeSendDataMessage('system', JSON.stringify({
          type: 'RECORDING_STARTED'
        }));
      }
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  }, [activeMeetingId]);

  const stopRecording = useCallback(async () => {
    if (!activePipelineId) {
      console.warn('No active recording pipeline to stop');
      return;
    }
    try {
      const token = await getAccessToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      console.log('Stopping recording for pipeline:', activePipelineId);
      const response = await fetch(`/api/video/stop-recording`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          mediaPipelineId: activePipelineId,
          recordingId: activeRecordingDbId,
        })
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to stop recording');
      }
      console.log('Recording stopped successfully');
      setActivePipelineId(null);
      setActiveRecordingDbId(null);
      setIsRecording(false);

      // Notify other clients via data message that recording has stopped
      if (audioVideoRef.current) {
        audioVideoRef.current.realtimeSendDataMessage('system', JSON.stringify({
          type: 'RECORDING_STOPPED'
        }));
      }
    } catch (err) {
      console.error('Error stopping recording:', err);
    }
  }, [activePipelineId, activeRecordingDbId]);

  const startScreenShare = useCallback(async () => {
    if (!audioVideoRef.current) return;
    try {
      await audioVideoRef.current.startContentShareFromScreenCapture();
    } catch (e) {
      console.error('Error starting screen share:', e);
    }
  }, []);

  const stopScreenShare = useCallback(() => {
    if (audioVideoRef.current) {
      audioVideoRef.current.stopContentShare();
    }
  }, []);

  const sendChatMessage = useCallback((text: string) => {
    if (audioVideoRef.current) {
      const sender = 'User';
      const payload = JSON.stringify({
        text,
        sender,
        timestamp: Date.now()
      });
      audioVideoRef.current.realtimeSendDataMessage('chat', payload);
      // Also add to local chat messages so the sender sees their own message
      setChatMessages(prev => [...prev, { text, sender, timestamp: Date.now() }]);
    }
  }, []);

  const sendCaption = useCallback((text: string) => {
    if (audioVideoRef.current) {
      const payload = JSON.stringify({
        text,
        timestamp: Date.now()
      });
      audioVideoRef.current.realtimeSendDataMessage('caption', payload);
    }
  }, []);

  return {
    localVideoRef,
    remoteVideoRef,
    isConnected,
    isPublishing,
    connectionQuality,
    error,
    activeMeetingId,
    connect,
    disconnect,
    toggleAudio,
    toggleVideo,
    rebindVideo,
    audioEnabled,
    videoEnabled,
    isRemoteVideoActive,
    startRecording,
    stopRecording,
    startScreenShare,
    stopScreenShare,
    sendChatMessage,
    chatMessages,
    isRecording,
    sendCaption,
    remoteCaption
  };
}